/**
 * Apply a named variant or package recipe to all product members of a group.
 */
import { useEffect, useMemo, useState } from "react";
import {
  ghostBtnClass,
  inputClass,
  mfgPrimaryBtnClass,
  mfgSecondaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import { attachPackageToRecipes } from "@/lib/manufacturing/attachPackageToRecipes";
import { productMemberIds } from "@/lib/manufacturing/groupMembers";
import {
  applyManufacturingNamedVariantToRecipes,
  listManufacturingNamedVariants,
  type NamedVariantSummary,
} from "@/lib/manufacturing/namedVariantsClient";
import type { MfgRecipeSummary } from "@/lib/manufacturing/types";

export function ManufacturingModelsGroupApplyShared({
  recipeGroupId,
  recipes,
  onDone,
}: {
  recipeGroupId: string;
  recipes: readonly MfgRecipeSummary[];
  onDone: (message: string) => void;
}) {
  const [mode, setMode] = useState<"variant" | "package" | null>(null);
  const [variants, setVariants] = useState<NamedVariantSummary[]>([]);
  const [namedVariantId, setNamedVariantId] = useState("");
  const [packageId, setPackageId] = useState("");
  const [description, setDescription] = useState(
    "Apply shared series variant to all group product members",
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const memberIds = useMemo(
    () => productMemberIds(recipes, recipeGroupId),
    [recipes, recipeGroupId],
  );
  const packages = useMemo(
    () => recipes.filter((r) => r.kind === "package" && r.is_active),
    [recipes],
  );

  useEffect(() => {
    if (mode !== "variant") return;
    void listManufacturingNamedVariants().then((r) => {
      if (r.ok) {
        setVariants(r.data.variants.filter((v) => v.status === "active" || v.status === "draft"));
      }
    });
  }, [mode]);

  async function applyVariant() {
    if (!namedVariantId || memberIds.length === 0) return;
    setBusy(true);
    setError(null);
    const r = await applyManufacturingNamedVariantToRecipes({
      named_variant_id: namedVariantId,
      parent_recipe_ids: memberIds,
      description: description.trim() || "Shared group variant",
    });
    setBusy(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setMode(null);
    onDone(
      `Applied variant to ${r.data.applications.length} product${r.data.applications.length === 1 ? "" : "s"} (pending confirm).`,
    );
  }

  async function applyPackage() {
    if (!packageId || memberIds.length === 0) return;
    setBusy(true);
    setError(null);
    const results = await attachPackageToRecipes(memberIds, packageId);
    setBusy(false);
    const failed = results.filter((x) => !x.ok);
    if (failed.length) {
      setError(failed[0] && !failed[0].ok ? failed[0].error : "Attach failed");
      return;
    }
    const attached = results.filter((x) => x.ok && !x.skipped).length;
    const skipped = results.filter((x) => x.ok && x.skipped).length;
    setMode(null);
    onDone(
      `Package attached to ${attached} product${attached === 1 ? "" : "s"}${skipped ? ` (${skipped} already had it)` : ""}.`,
    );
  }

  if (!mode) {
    return (
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className={mfgSecondaryBtnClass}
          disabled={memberIds.length === 0}
          onClick={() => setMode("variant")}
        >
          Apply shared variant to all
        </button>
        <button
          type="button"
          className={mfgSecondaryBtnClass}
          disabled={memberIds.length === 0 || packages.length === 0}
          onClick={() => setMode("package")}
        >
          Apply shared package to all
        </button>
        {memberIds.length === 0 ? (
          <span className="text-xs text-slate-500">No active product members yet.</span>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-xl border border-slate-200 p-3 dark:border-slate-700">
      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-2 py-1.5 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100">
          {error}
        </div>
      ) : null}
      <div className="text-sm font-medium text-slate-800 dark:text-slate-100">
        {mode === "variant" ? "Apply named variant" : "Attach package"} · {memberIds.length}{" "}
        product{memberIds.length === 1 ? "" : "s"}
      </div>
      {mode === "variant" ? (
        <>
          <label className="block text-sm">
            <span className="text-slate-600 dark:text-slate-300">Named variant</span>
            <select
              className={`${inputClass} mt-1`}
              value={namedVariantId}
              onChange={(e) => setNamedVariantId(e.target.value)}
            >
              <option value="">Select…</option>
              {variants.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name} ({v.code})
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            <span className="text-slate-600 dark:text-slate-300">Description</span>
            <textarea
              className={`${inputClass} mt-1 min-h-[64px]`}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>
        </>
      ) : (
        <label className="block text-sm">
          <span className="text-slate-600 dark:text-slate-300">Package recipe</span>
          <select
            className={`${inputClass} mt-1`}
            value={packageId}
            onChange={(e) => setPackageId(e.target.value)}
          >
            <option value="">Select…</option>
            {packages.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.code})
              </option>
            ))}
          </select>
        </label>
      )}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className={mfgPrimaryBtnClass}
          disabled={busy || (mode === "variant" ? !namedVariantId : !packageId)}
          onClick={() => void (mode === "variant" ? applyVariant() : applyPackage())}
        >
          {busy ? "Working…" : "Apply"}
        </button>
        <button
          type="button"
          className={ghostBtnClass}
          disabled={busy}
          onClick={() => {
            setMode(null);
            setError(null);
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
